import { FC } from 'react';
import { useTheme } from '@mui/material/styles';
import { Component } from "../../types/ContentStructure";
import { Box, Typography } from "@mui/material";
import ComponentLink from "./ComponentLink";
import { generateColors, generateLinearGradient } from "../../utils/utils";

interface IComponentBannerProductoProps {
    component: Component;
    mini?: boolean;
}

/**
 * BANNER PRODUCTO (componentTypeId=37)
 *
 * Hero full-width para páginas de producto (ProductPage).
 * Basado en ComponentCarrusel pero SIN padding/container/margin —
 * toca los bordes de la ventana en todos los breakpoints.
 *
 * Layout:
 *   Desktop/Tablet: panel izq (gradiente+texto+CTA) | panel der (imagen cover)
 *   Mobile: mismo layout pero aspect-ratio 1/1 que comprime ambos paneles
 *
 * Datos BD (ej. componentId=253 Crédito PyME):
 *   title    → título principal  ("Crédito PyME")
 *   text     → descripción corta
 *   image    → foto panel derecho
 *   icon     → SVG isotipo decorativo panel izq
 *   theme    → color de fondo del panel izq (themeId=9 → #6E1B17 rojo oscuro)
 *   components[] → LINKs CTA ("Simula tu crédito")
 */
const ComponentBannerProducto: FC<IComponentBannerProductoProps> = ({ component, mini }) => {
    const themeSys = useTheme();
    const { title, subTitle, text, icon, image, components } = component;
    const titleImage = components?.find((item: Component) => item.componentType.name === 'IMAGEN' && item.image)
    const ctas = components?.filter((item: Component) => item.componentType.name === 'LINK') || []

    return (
        /* Sin padding ni margin — full-width hasta los bordes */
        <Box sx={{
            width: '100%',
            /* Mobile: aspecto 1/1 comprimido — Desktop/Tablet: altura natural por contenido */
            minHeight: { sm: 320, md: 480, lg: 560 },
            //maxHeight: { sm: 320, md: 480, lg: 560 },
            display: 'flex',
            flexDirection: 'row',
            //overflow: 'hidden',
        }}>
            {/* ── Panel izquierdo: gradiente + ícono + texto + CTAs ── */}
            {/* Mobile: ocupa 100% del ancho — panel derecho oculto */}
            <Box sx={{
                position: 'relative',
                width: { xs: '100%', sm: 'fit-content', md: 'fit-content', lg: 'fit-content' },
                aspectRatio: '1/1',
                flexShrink: 0,
                display: 'flex',
                flexDirection: 'column',
                gap: { xs: 1, sm: 2, md: 3 },
                justifyContent: { xs: 'center', sm: 'center', md: 'center' },
                alignItems: { xs: 'center', sm: 'center', md: 'flex-start' },
                px: { xs: 1.5, sm: 2, md: 4, lg: 6 },
                py: { xs: 2, sm: 3, md: 5, lg: 7 },
                userSelect: 'none',
                //overflow: 'hidden',
            }}>
                {/* Ícono decorativo de fondo */}

                {icon &&
                    <Box sx={{
                        position: 'absolute',
                        inset: 0,
                        display: 'flex',
                        zIndex: 0,
                    }}>
                        <img
                            src={icon || ''}
                            alt=""
                            draggable={false}
                            style={{
                                width: 'auto',
                                height: '100%',
                                userSelect: 'none',
                                pointerEvents: 'none'
                            }}
                        />
                    </Box>
                }

                {/* Texto */}
                <Box sx={{
                    position: 'relative',
                    zIndex: 1,
                    mb: { xs: 1, sm: 2, md: 3 },
                    textAlign: { xs: 'center', sm: 'center', md: 'left' },
                }}>

                    {titleImage?.image ? (
                        <Box
                            component="img"
                            src={titleImage.image}
                            alt={titleImage.title || title || 'Titulo'}
                            sx={{
                                display: 'block',
                                width: '100%',
                                maxWidth: { xs: 260, sm: 320, md: 420, lg: 520 },
                                mb: { xs: 1.5, md: 2 },
                                mx: { xs: 'auto', md: 0, lg: 0 },
                                objectFit: 'contain',
                            }}
                        />
                    ) : title && (
                        <Typography variant="h1" sx={{
                            color: 'white',
                            mt: { xs: 0, sm: 0, md: 10 },
                            mb: 0.5,
                            lineHeight: 1.2,
                        }}>
                            {title}
                        </Typography>
                    )}
                    {subTitle && (
                        <Typography variant="subtitle1" sx={{
                            color: 'rgba(255,255,255,0.9)',
                            mb: 0.5,
                            fontSize: {
                                xs: themeSys.typography.caption.fontSize,
                                md: themeSys.typography.subtitle1.fontSize,
                            },
                        }}>
                            {subTitle}
                        </Typography>
                    )}
                    {text && (
                        <Typography variant="h3" sx={{
                            color: 'rgba(255,255,255,0.85)',
                            maxWidth: '36ch',
                            display: 'block',
                            lineHeight: 1.4,
                            fontWeight: 100,
                        }}>
                            {text}
                        </Typography>
                    )}
                </Box>

                {/* CTAs */}
                {ctas && ctas.length > 0 && (
                    <Box sx={{
                        position: 'relative',
                        zIndex: 1,
                        display: 'flex',
                        flexWrap: 'wrap',
                        gap: 1,
                    }}>
                        {ctas.map((item: Component, idx: number) => (
                            <ComponentLink key={idx} component={item} variant="cta" />
                        ))}
                    </Box>
                )}
            </Box>

            {/* ── Panel derecho: imagen cover — oculto en mobile ── */}
            <Box sx={{
                position: 'relative',
                zIndex: -10,
                display: { xs: 'none', sm: 'block' },
                width: '100%',
            }}>
                {image && (
                    <img
                        src={image}
                        alt={title ?? ''}
                        draggable={false}
                        style={{
                            width: '100%',
                            height: '100%',
                            objectFit: 'cover',
                            display: 'block',
                            userSelect: 'none',
                            pointerEvents: 'none'
                        }}
                    />
                )}
            </Box>
        </Box>
    )
}

export default ComponentBannerProducto
